import { useState, useEffect } from 'react';

export type TutorialType = 'game-basics' | 'advanced-rules' | 'multiplayer';

interface TutorialSettings {
  completedTutorials: TutorialType[];
  disabledTutorials: TutorialType[];
  showFirstGameTutorial: boolean;
}

const TUTORIAL_SETTINGS_KEY = 'lead-card-tutorial-settings';

const defaultSettings: TutorialSettings = {
  completedTutorials: [],
  disabledTutorials: [],
  showFirstGameTutorial: true
};

export function useTutorialSettings() {
  const [settings, setSettings] = useState<TutorialSettings>(defaultSettings);
  
  // Load settings from localStorage on mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        // First, try loading from the main settings key
        const storedSettings = localStorage.getItem(TUTORIAL_SETTINGS_KEY);
        
        if (storedSettings) {
          const parsedSettings = JSON.parse(storedSettings) as Partial<TutorialSettings>;
          
          // Check for data corruption in arrays
          let needsRepair = false;
          
          // Check if arrays contain invalid data (non-string values)
          if (Array.isArray(parsedSettings.completedTutorials)) {
            const validCompletedTutorials = parsedSettings.completedTutorials.filter(
              item => typeof item === 'string'
            );
            if (validCompletedTutorials.length !== parsedSettings.completedTutorials.length) {
              needsRepair = true;
              console.warn('Found corrupt completed tutorials array, repairing...');
              parsedSettings.completedTutorials = validCompletedTutorials;
            }
          }
          
          if (Array.isArray(parsedSettings.disabledTutorials)) {
            const validDisabledTutorials = parsedSettings.disabledTutorials.filter(
              item => typeof item === 'string'
            );
            if (validDisabledTutorials.length !== parsedSettings.disabledTutorials.length) {
              needsRepair = true;
              console.warn('Found corrupt disabled tutorials array, repairing...');
              parsedSettings.disabledTutorials = validDisabledTutorials;
            }
          }
          
          // Validate the structure of the stored settings
          const validatedSettings: TutorialSettings = {
            completedTutorials: Array.isArray(parsedSettings.completedTutorials) 
              ? parsedSettings.completedTutorials as TutorialType[]
              : defaultSettings.completedTutorials,
            disabledTutorials: Array.isArray(parsedSettings.disabledTutorials)
              ? parsedSettings.disabledTutorials as TutorialType[]
              : defaultSettings.disabledTutorials,
            showFirstGameTutorial: typeof parsedSettings.showFirstGameTutorial === 'boolean'
              ? parsedSettings.showFirstGameTutorial
              : defaultSettings.showFirstGameTutorial
          };
          
          // Now check for any individual tutorial completion markers
          // This is our backup mechanism for reliability
          const tutorialTypes: TutorialType[] = ['game-basics', 'advanced-rules', 'multiplayer'];
          let backupUpdatesNeeded = false;
          
          tutorialTypes.forEach(type => {
            const backupKey = `${TUTORIAL_SETTINGS_KEY}-${type}-completed`;
            const backupValue = localStorage.getItem(backupKey);
            
            if (backupValue && !validatedSettings.completedTutorials.includes(type)) {
              console.log(`Found backup completion marker for ${type}, restoring...`);
              validatedSettings.completedTutorials.push(type);
              backupUpdatesNeeded = true;
            }
          });
          
          // Remove any duplicate entries using filter instead of Set
          validatedSettings.completedTutorials = validatedSettings.completedTutorials.filter(
            (value, index, self) => self.indexOf(value) === index
          );
          validatedSettings.disabledTutorials = validatedSettings.disabledTutorials.filter(
            (value, index, self) => self.indexOf(value) === index
          );
          
          // If we made any repairs, save the fixed settings back to localStorage
          if (needsRepair || backupUpdatesNeeded) {
            localStorage.setItem(TUTORIAL_SETTINGS_KEY, JSON.stringify(validatedSettings));
            console.log('Repaired and saved tutorial settings.');
          }
          
          setSettings(validatedSettings);
          console.log('Loaded tutorial settings:', validatedSettings);
        } else {
          // No settings found in storage, check for backup markers
          const tutorialTypes: TutorialType[] = ['game-basics', 'advanced-rules', 'multiplayer'];
          const restoredSettings = { ...defaultSettings };
          let restoredAny = false;
          
          tutorialTypes.forEach(type => {
            const backupKey = `${TUTORIAL_SETTINGS_KEY}-${type}-completed`;
            const backupValue = localStorage.getItem(backupKey);
            
            if (backupValue) {
              console.log(`Found backup completion marker for ${type}, restoring settings...`);
              restoredSettings.completedTutorials.push(type);
              restoredAny = true;
            }
          });
          
          if (restoredAny) {
            localStorage.setItem(TUTORIAL_SETTINGS_KEY, JSON.stringify(restoredSettings));
            setSettings(restoredSettings);
            console.log('Restored tutorial settings from backup markers:', restoredSettings);
          } else {
            setSettings(defaultSettings);
          }
        }
      } catch (error) {
        console.error('Failed to load tutorial settings:', error);
        // If there's an error, reset to defaults
        setSettings(defaultSettings);
        
        // Try to diagnose and fix the error
        try {
          // Remove potentially corrupted data
          localStorage.removeItem(TUTORIAL_SETTINGS_KEY);
          console.log('Removed corrupted tutorial settings');
          
          // Check for the backup markers instead
          const tutorialTypes: TutorialType[] = ['game-basics', 'advanced-rules', 'multiplayer'];
          const restoredSettings = { ...defaultSettings };
          let restoredAny = false;
          
          tutorialTypes.forEach(type => {
            const backupKey = `${TUTORIAL_SETTINGS_KEY}-${type}-completed`;
            const backupValue = localStorage.getItem(backupKey);
            
            if (backupValue) {
              console.log(`Found backup completion marker for ${type}, restoring settings...`);
              restoredSettings.completedTutorials.push(type);
              restoredAny = true;
            }
          });
          
          if (restoredAny) {
            localStorage.setItem(TUTORIAL_SETTINGS_KEY, JSON.stringify(restoredSettings));
            setSettings(restoredSettings);
            console.log('Restored tutorial settings from backup markers:', restoredSettings);
          }
        } catch (err) {
          console.error('Failed to repair tutorial settings:', err);
        }
      }
    };
    
    loadSettings();
    
    // Add event listener for storage changes (in case settings are modified in another tab)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === TUTORIAL_SETTINGS_KEY || 
          event.key?.startsWith(`${TUTORIAL_SETTINGS_KEY}-`)) {
        loadSettings();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Save settings to localStorage whenever they change
  const saveSettings = (newSettings: TutorialSettings) => {
    try {
      // Make sure we're not saving duplicate entries by filtering
      const dedupedSettings: TutorialSettings = {
        ...newSettings,
        completedTutorials: newSettings.completedTutorials.filter(
          (value, index, self) => self.indexOf(value) === index
        ),
        disabledTutorials: newSettings.disabledTutorials.filter(
          (value, index, self) => self.indexOf(value) === index
        )
      };
      
      localStorage.setItem(TUTORIAL_SETTINGS_KEY, JSON.stringify(dedupedSettings));
      setSettings(dedupedSettings);
      console.log('Saved tutorial settings:', dedupedSettings);
    } catch (error) {
      console.error('Failed to save tutorial settings:', error);
    }
  };
  
  // Check if a tutorial has been completed
  const isTutorialCompleted = (tutorialType: TutorialType): boolean => {
    return settings.completedTutorials.includes(tutorialType);
  };
  
  // Check if a tutorial has been disabled
  const isTutorialDisabled = (tutorialType: TutorialType): boolean => {
    return settings.disabledTutorials.includes(tutorialType);
  };
  
  // Mark a tutorial as completed
  const markTutorialComplete = (tutorialType: TutorialType) => {
    console.log(`Marking tutorial ${tutorialType} as completed`);
    
    // Always attempt to save even if it appears to be already completed
    // This ensures we have recovery from any localStorage corruption
    const newSettings = {
      ...settings,
      completedTutorials: [...settings.completedTutorials.filter(t => t !== tutorialType), tutorialType]
    };
    
    // Double-check by immediately writing to localStorage directly
    try {
      localStorage.setItem(
        `${TUTORIAL_SETTINGS_KEY}-${tutorialType}-completed`, 
        new Date().toISOString()
      );
    } catch (error) {
      console.error(`Failed backup tutorial completion marker for ${tutorialType}:`, error);
    }
    
    // Save to the main settings object
    saveSettings(newSettings);
    
    return true;
  };
  
  // Disable a tutorial
  const disableTutorial = (tutorialType: TutorialType) => {
    if (!isTutorialDisabled(tutorialType)) {
      const newSettings = {
        ...settings,
        disabledTutorials: [...settings.disabledTutorials, tutorialType]
      };
      saveSettings(newSettings);
    }
  };
  
  // Enable a previously disabled tutorial
  const enableTutorial = (tutorialType: TutorialType) => {
    if (isTutorialDisabled(tutorialType)) {
      const newSettings = {
        ...settings,
        disabledTutorials: settings.disabledTutorials.filter(type => type !== tutorialType)
      };
      saveSettings(newSettings);
    }
  };
  
  // Toggle the first game tutorial setting
  const toggleFirstGameTutorial = () => {
    const newSettings = {
      ...settings,
      showFirstGameTutorial: !settings.showFirstGameTutorial
    };
    saveSettings(newSettings);
  };
  
  // Reset all tutorial settings
  const resetTutorialSettings = () => {
    saveSettings(defaultSettings);
  };
  
  return {
    settings,
    isTutorialCompleted,
    isTutorialDisabled,
    markTutorialComplete,
    disableTutorial,
    enableTutorial,
    toggleFirstGameTutorial,
    resetTutorialSettings
  };
}